import { Formik } from 'formik';
import { Card, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import massiveApi, {
  useCreateMassiveTaskMutation
} from '@/app/KeywordResearch/api/massiveApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';
import useDomain from '@/hooks/useDomain';
import { MassiveMainKeywordsInput } from './MassiveKeywordLinesInput';
import MassiveFormFiltersSection from './MassiveFormFiltersSection';
import MassiveFormFooter from './MassiveFormFooter';
import { useMassiveGeoSelect } from '@/app/KeywordResearch/hooks/useMassiveGeoSelect';
import {
  buildCreateTaskRequest,
  getKeywordDisplayError,
  getSubmitButtonState,
  LIMIT_MASSIVE_KEYWORDS,
  MASSIVE_FORM_INITIAL_VALUES,
  MASSIVE_HALF_SPACE_HINT,
  MASSIVE_KEYWORD_COUNT_HINT,
  massiveFormValidationSchema,
  hasHalfSpaceInKeywords,
  parseKeywords,
  shouldShowKeywordCountHint,
  type MassiveFormValues,
  type MassiveNewFormProps
} from './massiveFormConfig';

const MassiveNewForm = ({
  isHistoryLoading,
  massiveHistory
}: MassiveNewFormProps) => {
  useGetUserInfoQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { domain_slug } = useDomain();
  const [createTask, { isLoading }] = useCreateMassiveTaskMutation();
  const geo = useMassiveGeoSelect();

  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const hasAccess =
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  const handleSubmit = async (values: MassiveFormValues) => {
    const body = buildCreateTaskRequest(
      values,
      geo.selectedCountry.value,
      geo.selectedLanguage.value
    );

    try {
      const response = await createTask(body).unwrap();
      dispatch(massiveApi.util.invalidateTags(['historyMassive']));
      navigate(response.data);
    } catch (err) {
      console.log('Error on create massive task:', err);
    }
  };

  return (
    <Formik<MassiveFormValues>
      onSubmit={handleSubmit}
      initialValues={MASSIVE_FORM_INITIAL_VALUES}
      validationSchema={massiveFormValidationSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleSubmit: formikSubmit,
        setFieldValue
      }) => {
        const keywordCount = parseKeywords(values.keyword).length;
        const submitState = getSubmitButtonState({
          hasAccess,
          isLoading,
          isHistoryLoading,
          massiveHistory: massiveHistory?.data,
          isKeywordEmpty: keywordCount === 0,
          isKeywordOverLimit: keywordCount > LIMIT_MASSIVE_KEYWORDS
        });

        return (
          <Form onSubmit={formikSubmit}>
            <Card className="h-100">
              <Card.Body className="p-3 d-flex flex-column gap-3">
                <MassiveMainKeywordsInput
                  name="keyword"
                  values={values.keyword}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  limit={LIMIT_MASSIVE_KEYWORDS}
                  keywordCount={keywordCount}
                  displayError={getKeywordDisplayError(
                    keywordCount,
                    Boolean(touched.keyword),
                    errors.keyword
                  )}
                  helpTooltip={keywordResearchTooltips.massive.helper}
                  hints={[
                    shouldShowKeywordCountHint(values.keyword) &&
                      MASSIVE_KEYWORD_COUNT_HINT,
                    hasHalfSpaceInKeywords(values.keyword) &&
                      MASSIVE_HALF_SPACE_HINT
                  ].filter((hint): hint is string => Boolean(hint))}
                />
                <MassiveFormFiltersSection
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                />
                <MassiveFormFooter
                  values={values}
                  setFieldValue={setFieldValue}
                  countries={geo.countries}
                  languages={geo.languages}
                  selectedCountry={geo.selectedCountry}
                  selectedLanguage={geo.selectedLanguage}
                  onCountryChange={geo.handleCountryChange}
                  onLanguageChange={geo.setSelectedLanguage}
                  submitState={submitState}
                />
              </Card.Body>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};

export default MassiveNewForm;
